import { useTranslation } from "react-i18next";
import { FaGoogle, FaInstagram, FaLinkedin, FaTimes, FaYoutube } from "react-icons/fa";
import { Link, Outlet, useLocation } from "react-router-dom";

import logo from "../assets/blue_logo.svg";
import whatsappImage from "../assets/whatsapp-image.svg";
import AuthHeader from "../components/heading/AuthHeader";
import ZendeskWidget from "../components/ZendeskWidget";
import { getLanguage } from "../utils/getLanguage";

const AuthLayout = () => {
  const location = useLocation();

  const isPathLogin = location.pathname.includes("login");
  const isPathB12 = location.pathname.includes("b12");
  const isPathB2C = location.pathname.includes("b2c");
  const { t } = useTranslation();
  const lang = getLanguage();
  const isRtl = lang === "ar";

  return (
    <div
      className={`flex min-h-screen flex-col bg-blue-500 text-[#0a0a0a] ${isRtl && "rtl"}`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      {!isPathLogin && !isPathB12 && !isPathB2C && <AuthHeader />}
      {/* Conditionally render container */}
      {!isPathLogin && !isPathB12 && !isPathB2C ? (
        <main className="container mx-auto flex-grow px-4 sm:px-8">
          <Outlet />
        </main>
      ) : (
        <main className="flex-grow">
          <Outlet />
        </main>
      )}

      {!isPathB12 && !isPathLogin && !isPathB2C && (
        <footer className="border-t bg-white px-4 py-10 pb-12 text-sm sm:px-6 md:px-8">
          <div className="mx-auto grid max-w-7xl gap-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {/* Logo + Slogan */}
            <div>
              <Link to={`/${lang}`}>
                <img src={logo} alt="Hello World Kids" className="mb-4 h-12" />
              </Link>
              <p className="font-medium">{t("a_world_of_possibilities")}</p>

              <div className="mt-6">
                <p className="mb-2 font-semibold">{t("follow_us")}</p>
                <div className="flex flex-wrap gap-3">
                  <a href="mailto:Info@helloworldkids.org" target="_blank" rel="noopener noreferrer">
                    <IconCircle icon={<FaGoogle />} />
                  </a>
                  <a href="https://x.com/HelloWorldKids" target="_blank" rel="noopener noreferrer">
                    <IconCircle icon={<FaTimes />} />
                  </a>
                  <a
                    href="https://www.instagram.com/helloworldkids/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconCircle icon={<FaInstagram />} />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/helloworldkids/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconCircle icon={<FaLinkedin />} />
                  </a>
                  <a
                    href="https://www.youtube.com/channel/UCSq1Qb-HbzVYTxoQbPCVkSQ"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <IconCircle icon={<FaYoutube />} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="mb-2 font-bold">{t("contact_us")}</h3>
              <a href="tel:+962790488840" className="block hover:underline">
                {t("official_phone_number")}
              </a>
              <a href="mailto:Info@helloworldkids.org" className="mt-1 block hover:underline">
                info@helloworldkids.org
              </a>
              <a
                href="https://www.google.jo/maps/place/Hello+World+Kids/@31.9713151,35.8352919,17z"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 block hover:underline"
              >
                {t("address")}
              </a>
            </div>

            {/* Useful Links */}
            <div>
              <h3 className="mb-2 font-bold">{t("useful_links")}</h3>
              <ul className="space-y-1">
                <li>
                  <a href="#" className="hover:underline">
                    {t("courses")}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:underline">
                    {t("for_schools")}
                  </a>
                </li>
                <li>
                  <Link to={`/${lang}/terms`} className="hover:underline">
                    {t("terms_of_use")}
                  </Link>
                </li>
                <li>
                  <Link to={`/${lang}/privacy`} className="hover:underline">
                    {t("privacy")}
                  </Link>
                </li>
                <li>
                  <a
                    href="/static/Privacy_Policy.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {t("privacy_policy")}
                  </a>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h3 className="mb-2 font-bold">{t("our_newsletter")}</h3>
              <p className="mb-4">{t("subscribe_newsletter_text")}</p>
              <div className="flex">
                <input type="email" placeholder={t("your_email_address")} className="w-full border p-2" />
                <button className="w-32 bg-blue-500 px-4 py-2 font-bold text-white">{t("get_in")}</button>
              </div>
            </div>
          </div>
        </footer>
      )}
      <a
        href="https://wa.me/962790488840"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-3 z-50 flex items-center overflow-hidden rounded-full bg-green-500 text-white shadow-2xl ltr:right-20 sm:ltr:right-20 md:ltr:right-20 lg:ltr:right-[135px] rtl:left-20 sm:rtl:left-20 md:rtl:left-20 lg:rtl:left-[148px]"
      >
        <div className="flex h-12 w-12 items-center justify-center bg-green-400 text-white">
          <img src={whatsappImage} alt="WhatsApp" className="h-8 w-8" />
        </div>
      </a>
      <ZendeskWidget />
    </div>
  );
};

// Helper icon circle
const IconCircle = ({ icon }) => (
  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#fef7dd] text-xl text-black">
    {icon}
  </div>
);

export default AuthLayout;
